import {
  Component,
  HostBinding,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';

import { hexToRgb, rgbToHsv, rgbToHex } from './color-utils';

@Component({
  selector: 'suka-color-slider',
  template: `
    <canvas
      #canvas
      class="color-slider"
      width="20"
      height="250"
      (mousedown)="onMouseDown($event)"
      (mousemove)="onMouseMove($event)"
    >
    </canvas>
  `
})
export class ColorSlider implements OnChanges, AfterViewInit {
  @HostBinding('class.color-slider-wrapper') baseClass = true;
  private ctx: CanvasRenderingContext2D;
  private mousedown = false;
  private selectedHeight: number;

  @Input() hue: string;
  @Output() hueChange: EventEmitter<string> = new EventEmitter();

  @ViewChild('canvas', {static: false}) canvas: ElementRef<HTMLCanvasElement>;

  @HostListener('window:mouseup', ['$event'])
  onMouseUp(evt: MouseEvent) {
    this.mousedown = false;
  }

  draw() {
    if (!this.ctx) {
      this.ctx = this.canvas.nativeElement.getContext('2d');
    }

    const width = this.canvas.nativeElement.width;
    const height = this.canvas.nativeElement.height;
    this.ctx.clearRect(0, 0, width, height);

    const gradient = this.ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(255, 0, 0, 1)');
    gradient.addColorStop(0.17, 'rgba(255, 255, 0, 1)');
    gradient.addColorStop(0.34, 'rgba(0, 255, 0, 1)');
    gradient.addColorStop(0.51, 'rgba(0, 255, 255, 1)');
    gradient.addColorStop(0.68, 'rgba(0, 0, 255, 1)');
    gradient.addColorStop(0.85, 'rgba(255, 0, 255, 1)');
    gradient.addColorStop(1, 'rgba(255, 0, 0, 1)');

    this.ctx.beginPath();
    this.ctx.rect(0, 0, width, height);
    this.ctx.fillStyle = gradient;
    this.ctx.fill();
    this.ctx.closePath();

    if (this.selectedHeight) {
      this.ctx.beginPath();
      this.ctx.strokeStyle = 'white';
      this.ctx.lineWidth = 4;
      this.ctx.rect(0, this.selectedHeight - 5, width, 10);
      this.ctx.stroke();
      this.ctx.closePath();
    }
  }

  onMouseDown(evt: MouseEvent) {
    this.mousedown = true;
    this.selectedHeight = evt.offsetY;
    this.draw();
    this.emitHue(evt.offsetY);
  }

  onMouseMove(evt: MouseEvent) {
    if (this.mousedown) {
      this.selectedHeight = evt.offsetY;
      this.draw();
      this.emitHue(evt.offsetY);
    }
  }

  emitHue(y: number) {
    if (y < 0) {
      y = 0;
    } else if (y >= 250) {
      y = 249;
    }
    const color = this.getColorAtPosition(y);
    this.hueChange.emit(rgbToHex(color.r, color.g, color.b));
  }

  getColorAtPosition(y: number) {
    const imageData = this.ctx.getImageData(5, y, 1, 1).data;
    return {
      r: imageData[0],
      g: imageData[1],
      b: imageData[2],
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hue && !changes.hue.isFirstChange()) {
      const rgbColor = hexToRgb(this.hue);
      if (rgbColor) {
        const hsvColor = rgbToHsv(rgbColor.r, rgbColor.g, rgbColor.b);
        const hueFactor = hsvColor.h / 360;
        this.selectedHeight = hueFactor * 250;
        this.draw();
      }
    }
  }

  ngAfterViewInit() {
    if (this.hue) {
      const rgbColor = hexToRgb(this.hue);
      const hsvColor = rgbToHsv(rgbColor.r, rgbColor.g, rgbColor.b);
      const hueFactor = hsvColor.h / 360;
      this.selectedHeight = hueFactor * 250;
    }

    this.draw();
  }
}
